import { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useAuth } from '@/components/AuthProvider';
import { useCurrentUserProfile } from '@/hooks/useSupabaseUsers';
import { reportService, SupabaseReport } from '@/integrations/supabase/reportService';
import { useReportGroupsCompatible } from '@/hooks/useSupabaseReportGroupsOnly';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/useMediaQuery';

const PERIODS_PER_PAGE = 4;

function groupReportsByPeriod(reports: SupabaseReport[]): Record<string, SupabaseReport[]> {
  const grouped: Record<string, SupabaseReport[]> = {};
  reports.forEach(report => {
    const period = report.period || 'No Period';
    if (!grouped[period]) grouped[period] = [];
    grouped[period].push(report);
  });
  return grouped;
}

export function useMyReportView() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [periodPage, setPeriodPage] = useState(1);
  const [groupPeriodPage, setGroupPeriodPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set<any>());
  const [expandedPeriods, setExpandedPeriods] = useState(new Set<string>());
  const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile);
  const [isGroupSheetOpen, setIsGroupSheetOpen] = useState(false);

  // Current user identity
  const { data: profile } = useCurrentUserProfile();
  const authUserId = user?.id ?? null;
  const legacyUserId = profile?.legacy_id ?? null;

  // Periods cho "all" tab — tất cả nhóm
  const { data: allMyPeriods = [], isLoading: periodsLoading } = useQuery({
    queryKey: ['my-periods', authUserId, legacyUserId],
    queryFn: () => reportService.getMyPeriods(authUserId!, legacyUserId),
    enabled: !!authUserId,
    staleTime: 5 * 60 * 1000,
  });

  // Periods riêng cho group tab đang chọn
  const activeGroupId = activeTab !== 'all' ? activeTab : null;
  const { data: groupMyPeriods = [], isLoading: groupPeriodsLoading } = useQuery({
    queryKey: ['my-periods', authUserId, legacyUserId, activeGroupId],
    queryFn: () => reportService.getMyPeriods(authUserId!, legacyUserId, activeGroupId),
    enabled: !!authUserId && !!activeGroupId,
    staleTime: 5 * 60 * 1000,
  });

  // Chọn danh sách period phù hợp: all tab dùng allMyPeriods, group tab dùng groupMyPeriods
  const activePeriodList = activeTab === 'all' ? allMyPeriods : groupMyPeriods;
  const activePage = activeTab === 'all' ? periodPage : groupPeriodPage;
  const setActivePage = activeTab === 'all' ? setPeriodPage : setGroupPeriodPage;

  // Client-side period pagination
  const totalPages = Math.max(1, Math.ceil(activePeriodList.length / PERIODS_PER_PAGE));
  const currentPeriods = useMemo(
    () => activePeriodList.slice((activePage - 1) * PERIODS_PER_PAGE, activePage * PERIODS_PER_PAGE),
    [activePeriodList, activePage]
  );
  const periodPaginationInfo = {
    totalPages,
    totalPeriods: activePeriodList.length,
    page: activePage,
    hasNextPage: activePage < totalPages,
    hasPrevPage: activePage > 1,
  };

  // Fetch reports for current page periods (scoped to group nếu đang ở group tab)
  const { data: reports = [], isLoading: reportsLoading } = useQuery({
    queryKey: ['my-reports', authUserId, legacyUserId, currentPeriods, activeTab],
    queryFn: () => reportService.getMyReports(
      authUserId!, legacyUserId, currentPeriods,
      activeTab !== 'all' ? activeTab : null,
    ),
    enabled: !!authUserId && currentPeriods.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Tổng số báo cáo toàn bộ per group (không phân trang) — dùng cho sidebar badge
  const { data: totalGroupCounts } = useQuery({
    queryKey: ['my-report-group-counts', authUserId, legacyUserId],
    queryFn: () => reportService.getMyReportGroupCounts(authUserId!, legacyUserId),
    enabled: !!authUserId,
    staleTime: 5 * 60 * 1000,
  });

  // Report groups for sidebar
  const { reportGroups, loading: groupsLoading } = useReportGroupsCompatible();

  // getUserInfo — all reports belong to current user
  const getUserInfo = useCallback(() => ({
    name: profile?.user_name || user?.email?.split('@')[0] || 'Tôi',
    email: profile?.email || user?.email || '',
  }), [profile, user]);

  const formatDate = useCallback((date: any) => {
    if (!date) return 'N/A';
    try { return format(new Date(date), 'dd/MM/yyyy'); } catch { return 'N/A'; }
  }, []);

  // Filter by search
  const filteredReports = useMemo(() => {
    if (!searchTerm) return reports;
    const lower = searchTerm.toLowerCase();
    return reports.filter(r =>
      r.report_name?.toLowerCase().includes(lower) ||
      r.period?.toLowerCase().includes(lower) ||
      r.summary_by_ai?.toLowerCase().includes(lower)
    );
  }, [reports, searchTerm]);

  // paginatedPeriods — dùng cho "all" tab: tất cả report trên trang hiện tại
  const paginatedPeriods = useMemo(() => {
    const data = groupReportsByPeriod(
      filteredReports.filter(r => r.period && currentPeriods.includes(r.period))
    );
    return {
      data,
      totalPages: periodPaginationInfo.totalPages,
      totalPeriods: activePeriodList.length,
      currentPage: activePage,
      hasNextPage: periodPaginationInfo.hasNextPage,
      hasPrevPage: periodPaginationInfo.hasPrevPage,
    };
  }, [filteredReports, currentPeriods, activePeriodList.length, activePage, periodPaginationInfo]);

  // groupedReports cho sidebar (sidebar badge chỉ cần biết tổng per group trên trang hiện tại)
  const groupedReports = useMemo(() => {
    const grouped: Record<string, any> = {};
    reportGroups.forEach((group: any) => {
      const groupId = group.ID || group.GroupID || group.id;
      if (groupId) {
        grouped[groupId] = {
          ...group,
          ID: groupId,
          GroupID: groupId,
          reports: reports.filter(r => r.id_group === groupId),
        };
      }
    });
    return grouped;
  }, [reports, reportGroups]);

  // paginatedGroupPeriods — khi ở group tab, reports đã scoped theo group + periods rồi
  const paginatedGroupPeriods = useMemo(() => {
    if (activeTab === 'all') return { data: {}, totalPages: 0, totalPeriods: 0, currentPage: 1, hasNextPage: false, hasPrevPage: false };

    const groupReports = filteredReports.filter((r: any) =>
      r.period && currentPeriods.includes(r.period) &&
      r.id_group === activeTab
    );
    return {
      data: groupReportsByPeriod(groupReports),
      totalPages: periodPaginationInfo.totalPages,
      totalPeriods: activePeriodList.length,
      currentPage: activePage,
      hasNextPage: periodPaginationInfo.hasNextPage,
      hasPrevPage: periodPaginationInfo.hasPrevPage,
    };
  }, [activeTab, filteredReports, currentPeriods, activePeriodList.length, activePage, periodPaginationInfo]);

  const sortedReportGroups = useMemo(() =>
    [...Object.values(groupedReports)].sort((a: any, b: any) => (a.STT || 0) - (b.STT || 0)),
    [groupedReports]
  );

  // Report counts for sidebar badges
  const currentPageReportCounts = useMemo(() => {
    const total = filteredReports.filter(r => r.period && currentPeriods.includes(r.period)).length;
    const byGroup: Record<string, number> = {};
    Object.values(groupedReports).forEach((g: any) => {
      const groupId = (g.ID || g.id || '').toString();
      byGroup[groupId] = (g.reports || []).filter((r: any) => r.period && currentPeriods.includes(r.period)).length;
    });
    return { total, byGroup };
  }, [filteredReports, currentPeriods, groupedReports]);

  // Toggle handlers
  const toggleRowExpansion = useCallback((reportId: any) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(reportId) ? next.delete(reportId) : next.add(reportId);
      return next;
    });
  }, []);

  const togglePeriodExpansion = useCallback((period: string) => {
    setExpandedPeriods(prev => {
      const next = new Set(prev);
      next.has(period) ? next.delete(period) : next.add(period);
      return next;
    });
  }, []);

  const drawerIsOpen = isMobile ? isGroupSheetOpen : isDrawerOpen;

  const handleDrawerToggle = useCallback(() => {
    if (isMobile) setIsGroupSheetOpen(v => !v);
    else setIsDrawerOpen(v => !v);
  }, [isMobile]);

  const handleDrawerClose = useCallback(() => {
    if (isMobile) setIsGroupSheetOpen(false);
    else setIsDrawerOpen(false);
  }, [isMobile]);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setGroupPeriodPage(1); // reset trang khi đổi nhóm
  }, []);
  const handleNextPage = useCallback(() => setActivePage(p => Math.min(p + 1, totalPages)), [setActivePage, totalPages]);
  const handlePrevPage = useCallback(() => setActivePage(p => Math.max(p - 1, 1)), [setActivePage]);
  const handlePageChange = useCallback((page: number) => setActivePage(page), [setActivePage]);

  const openFileUrl = useCallback((url: string) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleDeleteReport = useCallback(async (reportId: string) => {
    const { error } = await supabase.from('reports').delete().eq('id', reportId);
    if (error) {
      toast({ title: 'Xóa thất bại', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Đã xóa báo cáo' });
      queryClient.invalidateQueries({ queryKey: ['my-reports'] });
      queryClient.invalidateQueries({ queryKey: ['my-periods'] });
    }
  }, [toast, queryClient]);

  // Auto-expand first period on load
  const handleAutoExpand = useCallback(() => {
    if (currentPeriods.length > 0 && expandedPeriods.size === 0) {
      setExpandedPeriods(new Set([currentPeriods[0]]));
    }
  }, [currentPeriods, expandedPeriods.size]);

  const totalReportCounts = useMemo(() => ({
    total: totalGroupCounts?.total ?? 0,
    byGroup: totalGroupCounts?.byGroup ?? {},
  }), [totalGroupCounts]);

  return {
    // Data
    reports,
    paginatedPeriods,
    paginatedGroupPeriods,
    groupedReports,
    sortedReportGroups,
    currentPageReportCounts,
    totalReportCounts,
    // State
    activeTab,
    searchTerm,
    expandedRows,
    expandedPeriods,
    drawerIsOpen,
    refreshingReports: new Set<any>(),
    // Loading
    loading: periodsLoading || (reportsLoading && currentPeriods.length > 0) || groupsLoading,
    // User info
    authUserId,
    getUserInfo,
    formatDate,
    // Handlers
    handleTabChange,
    handleDrawerToggle,
    handleDrawerClose,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    handleDeleteReport,
    handleAutoExpand,
    onSearchChange: setSearchTerm,
    toggleRowExpansion,
    togglePeriodExpansion,
    openFileUrl,
  };
}
